// Affiliate cookie tracking utilities

const COOKIE_NAME = 'grizzlee_ref';
const COOKIE_DURATION_DAYS = 30;

export interface ReferralData {
  affiliateCode: string;
  landingPage: string;
  referralUrl: string;
  timestamp: number;
}

/**
 * Set affiliate referral cookie from URL parameter
 */
export function setAffiliateCookie(affiliateCode: string): void {
  const referralData: ReferralData = {
    affiliateCode,
    landingPage: window.location.pathname,
    referralUrl: window.location.href,
    timestamp: Date.now(),
  };

  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + COOKIE_DURATION_DAYS);

  // Set HTTP cookie
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(referralData))}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;

  // Also store in localStorage as backup
  localStorage.setItem(COOKIE_NAME, JSON.stringify(referralData));

  console.log('[Affiliate] Cookie set for:', affiliateCode);
}

/**
 * Get affiliate referral data from cookie or localStorage
 */
export function getAffiliateCookie(): ReferralData | null {
  try {
    // Try cookie first
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === COOKIE_NAME && value) {
        return JSON.parse(decodeURIComponent(value));
      }
    }

    // Fallback to localStorage
    const stored = localStorage.getItem(COOKIE_NAME);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('[Affiliate] Error reading cookie:', error);
  }

  return null;
}

/**
 * Clear affiliate referral cookie
 */
export function clearAffiliateCookie(): void {
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  localStorage.removeItem(COOKIE_NAME);
}

/**
 * Check URL for affiliate referral parameter and set cookie
 */
export function checkAndSetAffiliateFromUrl(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref');

  if (refCode) {
    setAffiliateCookie(refCode);
    
    // Optionally clean URL without reloading
    const url = new URL(window.location.href);
    url.searchParams.delete('ref');
    window.history.replaceState({}, '', url.toString());
  }
}

/**
 * Check if affiliate cookie is still valid (not expired)
 */
export function isAffiliateCookieValid(): boolean {
  const data = getAffiliateCookie();
  if (!data) return false;

  const expiryTime = data.timestamp + (COOKIE_DURATION_DAYS * 24 * 60 * 60 * 1000);
  return Date.now() < expiryTime;
}
